import React from 'react';
import clsx from 'clsx';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import styles from './WizardStepper.module.scss';

interface WizardStepperProps {
    currentStep: number;
    /**
     * The furthest step the user has reached so far. Steps with id <= this
     * value are considered "reachable" and become clickable when `onStepClick`
     * is provided (typically all reached steps except the current one). When
     * omitted we treat it as `currentStep`, so only the current step is
     * highlighted and nothing is clickable — the wizard renders a read-only
     * progress indicator.
     */
    maxReachedStep?: number;
    /**
     * When provided, reached steps render as buttons and invoke this on click.
     * Parent is expected to validate / persist / navigate accordingly. When
     * omitted, the stepper is purely visual.
     */
    onStepClick?: (step: number) => void;
}

const steps = [
    {
        id: 1,
        label: 'Recipients'
    },
    {
        id: 2,
        label: 'Template'
    },
    {
        id: 3,
        label: 'Review & Send'
    }
];

export const WizardStepper: React.FC<WizardStepperProps> = ({ currentStep, maxReachedStep, onStepClick }) => {
    const reachedCeiling = maxReachedStep ?? currentStep;

    return (
        <div className={styles.stepper}>
            {steps.map((step, index) => {
                const isActive = currentStep === step.id;
                const isCompleted = currentStep > step.id;
                const isClickable = !isActive && step.id <= reachedCeiling && onStepClick !== undefined;

                const content = (
                    <>
                        <span
                            className={clsx(styles.stepNumber, {
                                [styles.activeStepNumber]: isActive,
                                [styles.completedStepNumber]: isCompleted
                            })}
                        >
                            {isCompleted ? <FontAwesomeIcon icon={faCheck} /> : step.id}
                        </span>

                        <span
                            className={clsx(styles.stepLabel, {
                                [styles.activeStepLabel]: isActive,
                                [styles.completedStepLabel]: isCompleted
                            })}
                        >
                            {step.label}
                        </span>
                    </>
                );

                return (
                    <React.Fragment key={step.id}>
                        {isClickable ? (
                            <button
                                type="button"
                                className={clsx(styles.step, styles.stepClickable)}
                                onClick={() => onStepClick!(step.id)}
                                aria-label={`Go to ${step.label}`}
                            >
                                {content}
                            </button>
                        ) : (
                            <div
                                className={styles.step}
                                aria-current={isActive ? 'step' : undefined}
                            >
                                {content}
                            </div>
                        )}

                        {index < steps.length - 1 && <div className={styles.separator} />}
                    </React.Fragment>
                );
            })}
        </div>
    );
};
